import React from "react"
import { PermissionTile } from "./RolePermissionsSelector"
import {
  DELIVERY_PARTNER_COUPLED_GROUPS,
  DELIVERY_PARTNER_GENERAL_PERMISSIONS,
  DELIVERY_PARTNER_PROFILE_ONLY_TABS,
  DELIVERY_PARTNER_VIEW_PERMISSION_ID,
  findPermission,
  findSubPermission,
  getAutoEnableSubIdsForManage,
  getLayoutHint,
  getLayoutLabel,
  isManageEnabledForViewSub,
} from "utils/deliveryPartnerPermissionLayout"

const Section = ({ title, hint, children }) => (
  <section className="role-permissions-split__dp-section">
    <div className="role-permissions-split__dp-section-head">
      <h6 className="role-permissions-split__dp-section-title">{title}</h6>
      {hint ? (
        <p className="role-permissions-split__dp-section-hint">{hint}</p>
      ) : null}
    </div>
    {children}
  </section>
)

const PermissionGroupCard = ({ title, hint, children }) => (
  <div className="role-permissions-split__dp-group">
    <div className="role-permissions-split__dp-group-head">
      <span className="role-permissions-split__dp-group-title">{title}</span>
      {hint ? (
        <span className="role-permissions-split__dp-group-hint">{hint}</span>
      ) : null}
    </div>
    <ul className="role-permissions-split__perm-list mb-0">{children}</ul>
  </div>
)

const DeliveryPartnerPermissionsEditor = ({
  permissions = [],
  language,
  t,
  onPermissionChange,
  getPermissionLabel,
}) => {
  const viewPermission = findPermission(permissions, DELIVERY_PARTNER_VIEW_PERMISSION_ID)
  const hasViewAccess = Boolean(viewPermission?.isEnabled)

  const labelForPermission = (permission, fallbackEntry) => {
    if (permission && getPermissionLabel) {
      return getPermissionLabel(permission, language)
    }
    return getLayoutLabel(fallbackEntry, language)
  }

  const handleManageChange = managePermissionId => {
    const managePermission = findPermission(permissions, managePermissionId)
    const willEnable = !managePermission?.isEnabled

    onPermissionChange(managePermissionId)

    if (willEnable) {
      getAutoEnableSubIdsForManage(managePermissionId).forEach(subId => {
        const subPermission = findSubPermission(viewPermission, subId)
        if (subPermission && !subPermission.isEnabled) {
          onPermissionChange(DELIVERY_PARTNER_VIEW_PERMISSION_ID, subId)
        }
      })
    }
  }

  const renderViewSubTile = (subEntry, requiresManageIds = []) => {
    const subPermission = findSubPermission(viewPermission, subEntry.subId)
    const manageAllowed = isManageEnabledForViewSub(permissions, requiresManageIds)
    const checked = Boolean(subPermission?.isEnabled)
    const disabled = !hasViewAccess

    return (
      <li key={subEntry.subId} className="role-permissions-split__perm-sub-wrap">
        <PermissionTile
          label={getLayoutLabel(subEntry, language)}
          checked={checked}
          isSub
          onChange={() => {
            if (!disabled) {
              onPermissionChange(DELIVERY_PARTNER_VIEW_PERMISSION_ID, subEntry.subId)
            }
          }}
        />
        {!manageAllowed && checked ? (
          <p className="role-permissions-split__dp-inline-note">
            {t(
              "Enable the manage permission below for this tab to work for users on this role."
            )}
          </p>
        ) : null}
        {disabled ? (
          <p className="role-permissions-split__dp-inline-note">
            {t("Enable View riders first.")}
          </p>
        ) : null}
      </li>
    )
  }

  const renderSimpleCoupledGroup = group => {
    const managePermission = findPermission(permissions, group.managePermissionId)

    return (
      <PermissionGroupCard
        key={group.id}
        title={getLayoutLabel(group, language)}
        hint={getLayoutHint(group, language)}
      >
        {renderViewSubTile(
          {
            subId: group.viewSubId,
            label: group.viewLabel,
            labelAr: group.viewLabelAr,
          },
          [group.managePermissionId]
        )}
        <li className="role-permissions-split__perm-sub-wrap">
          <PermissionTile
            label={getLayoutLabel(
              {
                label: group.manageLabel,
                labelAr: group.manageLabelAr,
              },
              language
            )}
            checked={Boolean(managePermission?.isEnabled)}
            isSub
            onChange={() => handleManageChange(group.managePermissionId)}
          />
        </li>
      </PermissionGroupCard>
    )
  }

  const renderEarningsGroup = group => (
    <PermissionGroupCard
      key={group.id}
      title={getLayoutLabel(group, language)}
      hint={getLayoutHint(group, language)}
    >
      {group.items.map(item => {
        if (item.type === "viewSub") {
          return renderViewSubTile(item, item.requiresManageIds)
        }

        const managePermission = findPermission(permissions, item.permissionId)
        return (
          <li key={item.permissionId} className="role-permissions-split__perm-sub-wrap">
            <PermissionTile
              label={getLayoutLabel(item, language)}
              checked={Boolean(managePermission?.isEnabled)}
              isSub
              onChange={() => handleManageChange(item.permissionId)}
            />
          </li>
        )
      })}
    </PermissionGroupCard>
  )

  return (
    <div className="role-permissions-split__dp-layout">
      <Section
        title={t("General access")}
        hint={t("Module-level access for the Delivery Partners area.")}
      >
        <ul className="role-permissions-split__perm-list mb-0">
          {DELIVERY_PARTNER_GENERAL_PERMISSIONS.map(entry => {
            const permission = findPermission(permissions, entry.permissionId)
            return (
              <li
                key={entry.permissionId}
                className="role-permissions-split__perm-item-wrap"
              >
                <PermissionTile
                  label={labelForPermission(permission, entry)}
                  checked={Boolean(permission?.isEnabled)}
                  onChange={() => onPermissionChange(entry.permissionId)}
                />
                {entry.description ? (
                  <p className="role-permissions-split__dp-inline-note">
                    {language === "ar" || language === "sa"
                      ? entry.descriptionAr || entry.description
                      : entry.description}
                  </p>
                ) : null}
              </li>
            )
          })}
        </ul>
      </Section>

      <Section
        title={t("Rider profile tabs")}
        hint={t(
          "Control which tabs appear when opening a rider profile. Requires View riders."
        )}
      >
        <ul className="role-permissions-split__perm-list mb-0">
          {DELIVERY_PARTNER_PROFILE_ONLY_TABS.map(tab =>
            renderViewSubTile(tab)
          )}
        </ul>
      </Section>

      <Section
        title={t("Tab + action permissions")}
        hint={t(
          "Each group pairs a profile tab with the related manage permission. Both are usually needed for full access."
        )}
      >
        {DELIVERY_PARTNER_COUPLED_GROUPS.map(group =>
          group.items ? renderEarningsGroup(group) : renderSimpleCoupledGroup(group)
        )}
      </Section>
    </div>
  )
}

export default DeliveryPartnerPermissionsEditor
